import { ENTRY_END_TITLES, type PlatformEvent } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Panel } from '../../ui/layout';
import { usePlatformRole } from './console-shared';

/**
 * Журнал портала (ЛЦ-05): лицензии, компании, заявки — кто, что и когда. Администратору портала — только действия
 * команды портала: входы в компании, люди, безопасность, объявления (ЛЦ-09…ЛЦ-13), без счетов и лицензий.
 */
export function EventsPage() {
  const owner = usePlatformRole() === 'owner';
  const q = useQuery({
    queryKey: ['platform', 'events', owner],
    queryFn: () =>
      api<PlatformEvent[]>(owner ? '/platform/events' : '/platform/console/events', {
        query: { limit: 300 },
      }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  return (
    <Panel title="Журнал" meta="последние 300 записей" bodyClassName="p-0">
      {!q.data.length ? (
        <div className="p-5">
          <EmptyState title="Записей пока нет">
            Здесь появятся выпуск и продление лицензий, создание компаний и заявки.
          </EmptyState>
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {q.data.map((e) => {
            const d = e.details as Record<string, unknown>;
            const bits: string[] = [];
            if (typeof d.planName === 'string') bits.push(`тариф «${d.planName}»`);
            if (typeof d.endsAt === 'string') bits.push(`до ${d.endsAt}`);
            if (typeof d.reason === 'string') bits.push(`причина: ${d.reason}`);
            if (typeof d.company === 'string' && !e.tenantName) bits.push(String(d.company));
            if (typeof d.status === 'string') bits.push(`статус: ${d.status}`);
            // Контрольная проверка модели Pilot (ИИ-13)
            if (typeof d.passed === 'number' && typeof d.total === 'number')
              bits.push(
                `${typeof d.model === 'string' ? `${d.model}: ` : ''}верно ${d.passed} из ${d.total}`,
              );
            if (d.trigger === 'auto') bits.push('запущена сама после смены модели');
            // Супер-админка (ЛЦ-09…ЛЦ-13): кто, режим, причина конца, ключ блокировки, заголовок объявления
            if (typeof d.fullName === 'string') bits.push(String(d.fullName));
            if (typeof d.mode === 'string') bits.push(d.mode === 'edit' ? 'с правкой' : 'просмотр');
            if (typeof d.endReason === 'string')
              bits.push(
                ENTRY_END_TITLES[d.endReason as keyof typeof ENTRY_END_TITLES] ?? String(d.endReason),
              );
            if (typeof d.key === 'string') bits.push(String(d.key));
            if (typeof d.title === 'string') bits.push(`«${d.title}»`);
            return (
              <li
                key={e.id}
                className="flex flex-col gap-0.5 px-5 py-2.5 text-[14px] sm:flex-row sm:items-baseline sm:gap-4"
              >
                <span className="num shrink-0 text-[12px] text-ink-2 sm:w-[132px]">
                  {dateTime(e.createdAt)}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-ink">{e.title}</span>
                  {e.tenantId && e.tenantName ? (
                    <>
                      {' · '}
                      <Link
                        to="/platform/tenants/$id"
                        params={{ id: e.tenantId }}
                        className="inline-flex min-h-11 items-center text-ink underline-offset-2 hover:underline md:min-h-0"
                      >
                        {e.tenantName}
                      </Link>
                    </>
                  ) : null}
                  {bits.length ? <span className="text-ink-2"> · {bits.join(', ')}</span> : null}
                </div>
                <span className="shrink-0 text-[13px] text-ink-2">{e.actorName ?? 'портал'}</span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
